import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetOrganizationsQuery } from '@/entities/organizations';
import { useUrlState, useProjectSiteParams, useDebounce } from '@/shared/lib';
import { compareFilters, isDefaultFilters } from '@/shared/lib/utils';
import type { SelectOption } from '@/shared/ui';

import type { ReceiveUrlState } from './types';
import { DEFAULT_URL_STATE } from './constants';
import { buildReceiveFilterChips } from './build-filter-chips';

export const useReceiveFilters = () => {
  const { t } = useTranslation();
  const { projectId } = useProjectSiteParams();
  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_URL_STATE);
  const [localFilters, setLocalFilters] = useState<ReceiveUrlState>(() => urlState);
  const [searchQuery, setSearchQuery] = useState(() => urlState.search);
  const [organizationSearch, setOrganizationSearch] = useState('');

  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const debouncedOrganizationSearch = useDebounce(organizationSearch, 300);

  const { data: organizationsData, isLoading: isLoadingOrganizations } = useGetOrganizationsQuery(
    { search: debouncedOrganizationSearch || undefined, projectId },
    { skip: !projectId }
  );

  const organizationOptions: SelectOption[] = useMemo(() => {
    if (!organizationsData?.organizations) return [];
    return organizationsData.organizations.map((org) => ({
      value: org.id.toString(),
      label: org.name,
      keywords: org.abbreviation ? [org.abbreviation] : undefined
    }));
  }, [organizationsData]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    (['consignmentNumber', 'organization', 'originLocation', 'shipmentIdentification'] as const).forEach((key) => {
      if (urlState[key] && urlState[key] !== DEFAULT_URL_STATE[key]) count++;
    });
    return count;
  }, [urlState]);

  const hasActiveFilters = activeFiltersCount > 0;

  const isFiltersDirty = useMemo(() => !compareFilters(localFilters, urlState, ['search']), [localFilters, urlState]);
  const hasDefaultFilters = useMemo(() => isDefaultFilters(urlState, DEFAULT_URL_STATE, ['search']), [urlState]);

  const handleFilterChange = useCallback((key: keyof ReceiveUrlState, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setSelectedOrganization = useCallback((value?: string) => {
    setLocalFilters((prev) => ({ ...prev, organization: value || '' }));
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleReset = useCallback(() => {
    setLocalFilters(DEFAULT_URL_STATE);
    setSearchQuery('');
    resetUrlState();
  }, [resetUrlState]);

  const removeFilter = useCallback(
    (key: string) => {
      setUrlState({ [key]: DEFAULT_URL_STATE[key as keyof ReceiveUrlState] });
    },
    [setUrlState]
  );

  const activeFilters = useMemo(
    () => buildReceiveFilterChips({ state: urlState, t, organizationOptions, onRemove: removeFilter }),
    [urlState, t, organizationOptions, removeFilter]
  );

  const handleApplyFilters = useCallback(() => {
    const { search, ...filtersWithoutSearch } = localFilters;
    setUrlState(filtersWithoutSearch);
  }, [localFilters, setUrlState]);

  useEffect(() => {
    setLocalFilters(urlState);
  }, [urlState]);

  useEffect(() => {
    if (debouncedSearchQuery !== urlState.search) {
      setUrlState({ search: debouncedSearchQuery });
    }
  }, [debouncedSearchQuery, setUrlState, urlState.search]);

  return {
    urlState: localFilters,
    activeFilters,
    searchQuery,
    activeFiltersCount,
    hasActiveFilters,
    isFiltersDirty,
    hasDefaultFilters,

    organizationOptions,
    isLoadingOrganizations,
    organizationSearch,
    setOrganizationSearch,
    setSelectedOrganization,

    handleFilterChange,
    handleSearchChange,
    handleReset,
    handleApplyFilters
  };
};
